import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Sparkles, KeyRound } from "lucide-react";
import MysticBackground from "@/components/effects/MysticBackground";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenido al Grimoire");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-zinc-100 grid md:grid-cols-5 relative overflow-hidden bg-black">
      {/* Ambient background — persistent across the panel */}
      <MysticBackground />

      {/* Left — brand ritual (3 cols) */}
      <div className="hidden md:flex md:col-span-3 relative flex-col justify-between p-12 z-10">
        {/* Vertical divider gold line */}
        <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-gold-antique/40 to-transparent" />

        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-4"
        >
          <div className="relative w-12 h-12 flex items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-gold-antique/60 via-transparent to-violet-mystic/60 blur-lg" />
            <span className="absolute inset-1 rounded-full border border-gold-antique/60 bg-black" />
            <Sparkles size={20} className="relative text-gold-antique animate-gold-pulse" strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-topsecret font-black text-3xl tracking-[0.14em] backlight-gold neon-flicker">
              IARISK
            </div>
            <div className="font-bingbam text-sm italic text-violet-mystic/85 tracking-normal mt-0.5">
              Grimoire
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="space-y-6 max-w-md"
        >
          <div className="text-[11px] uppercase tracking-[0.4em] font-mono text-gold-antique/70">
            · Command · Ritual · Control ·
          </div>
          <h1 className="font-topsecret text-6xl xl:text-7xl leading-[1.05] tracking-[0.02em] backlight-gold neon-flicker">
            INVOCA<br />
            EL PODER<br />
            DEL ORÁCULO.
          </h1>
          <p className="font-bingbam text-xl italic leading-relaxed text-zinc-300/90 max-w-sm">
            Administra créditos, aprueba pagos y ejecuta rituales sobre
            usuarios y paquetes — un santuario privado para quien maneja
            el bot de <span className="backlight-soft-red not-italic">IARisk</span>.
          </p>
          <div className="flex items-center gap-2 text-[10px] uppercase text-zinc-600 tracking-[0.35em] font-mono">
            <span className="h-px w-8 bg-gold-antique/50" />
            Solo iniciados
            <span className="h-px w-8 bg-gold-antique/50" />
          </div>
        </motion.div>

        <div className="text-[10px] uppercase tracking-[0.4em] font-mono text-zinc-700">
          Operator Console · MMXXVI
        </div>
      </div>

      {/* Right — form (2 cols) */}
      <div className="md:col-span-2 flex items-center justify-center p-6 sm:p-10 relative z-10">
        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          className="w-full max-w-sm relative"
          data-testid="login-form"
        >
          {/* Velvet glass card */}
          <div className="relative bg-black/70 backdrop-blur-2xl border border-gold-antique/20 rounded-lg p-7 shadow-[0_0_60px_-15px_rgba(212,175,55,0.35)]">
            {/* Corner glimmers */}
            <span className="pointer-events-none absolute -top-px -left-px h-6 w-6 border-t border-l border-gold-antique/70 rounded-tl-lg" />
            <span className="pointer-events-none absolute -top-px -right-px h-6 w-6 border-t border-r border-gold-antique/70 rounded-tr-lg" />
            <span className="pointer-events-none absolute -bottom-px -left-px h-6 w-6 border-b border-l border-gold-antique/70 rounded-bl-lg" />
            <span className="pointer-events-none absolute -bottom-px -right-px h-6 w-6 border-b border-r border-gold-antique/70 rounded-br-lg" />

            {/* Mobile brand */}
            <div className="md:hidden flex items-center gap-3 mb-6">
              <div className="relative w-10 h-10 flex items-center justify-center">
                <span className="absolute inset-0 rounded-full bg-gradient-to-br from-gold-antique/50 to-violet-mystic/50 blur-md" />
                <span className="absolute inset-1 rounded-full border border-gold-antique/50 bg-black" />
                <Sparkles size={16} className="relative text-gold-antique animate-gold-pulse" strokeWidth={1.6} />
              </div>
              <div>
                <div className="font-topsecret font-black text-2xl backlight-gold neon-flicker tracking-[0.12em]">
                  IARISK
                </div>
                <div className="font-bingbam italic text-sm text-violet-mystic mt-0.5">
                  Grimoire
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-gold-antique/70 mb-2">
                · Acceso ·
              </div>
              <h2 className="font-topsecret text-4xl tracking-[0.04em] backlight-gold">
                INICIAR SESIÓN
              </h2>
              <p className="font-bingbam italic text-base text-zinc-400 mt-3">
                Solo para archimages autorizados.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-[10px] uppercase font-mono tracking-[0.3em] text-gold-antique/75 mb-2 block">
                  · Correo ·
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@correo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="bg-black/60 border-gold-antique/25 rounded-md focus-visible:border-gold-antique focus-visible:ring-1 focus-visible:ring-gold-antique/40 h-11 text-zinc-100 placeholder:text-zinc-600 font-mono"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-[10px] uppercase font-mono tracking-[0.3em] text-gold-antique/75 mb-2 block">
                  · Sello secreto ·
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="bg-black/60 border-gold-antique/25 rounded-md focus-visible:border-gold-antique focus-visible:ring-1 focus-visible:ring-gold-antique/40 h-11 pr-10 text-zinc-100 placeholder:text-zinc-600 font-mono"
                  />
                  <KeyRound size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gold-antique/60" strokeWidth={1.6} />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="mt-6 w-full h-11 bg-gradient-to-b from-gold-antique to-gold-dim hover:from-gold-pale hover:to-gold-antique text-black rounded-md font-topsecret tracking-[0.18em] uppercase text-base shadow-[0_0_25px_rgba(212,175,55,0.55)] hover:shadow-[0_0_45px_rgba(212,175,55,0.85)] transition-all disabled:opacity-60"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>ENTRAR AL GRIMOIRE</>
              )}
            </Button>

            <p className="text-[10px] uppercase tracking-[0.3em] font-mono text-zinc-600 text-center mt-6">
              · Solo iniciados ·
            </p>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
